import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Breadcrumbs,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
} from '@mui/material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import type { Backend, VirtualBucket, Credential, Account } from '@/types'

interface Props {
  backend: Backend
  virtualBucket: VirtualBucket & { credential_ids?: number[] }
  availableCredentials: Credential[]
}

export default function VirtualBucketsEdit({ backend, virtualBucket, availableCredentials }: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props

  const { data, setData, patch, processing, errors } = useForm({
    name: virtualBucket.name || '',
    base_path: virtualBucket.base_path || '',
    public_read: virtualBucket.public_read || false,
    public_list: virtualBucket.public_list || false,
    credential_ids: virtualBucket.credential_ids || [],
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/virtual_buckets/${virtualBucket.id}`)
  }

  const toggleCredential = (id: number) => {
    setData('credential_ids',
      data.credential_ids.includes(id)
        ? data.credential_ids.filter(c => c !== id)
        : [...data.credential_ids, id]
    )
  }

  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
          <Link href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}>{backend.name}</Link>
          <Link href={`/virtual_buckets/${virtualBucket.id}`}>{virtualBucket.name}</Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600}>
            Edit Virtual Bucket
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update {virtualBucket.name} settings.
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Name"
              fullWidth
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || 'The name clients will use to access this virtual bucket.'}
              placeholder="my-virtual-bucket"
              sx={{ mb: 3 }}
            />

            <TextField
              label="Base Path"
              fullWidth
              value={data.base_path}
              onChange={(e) => setData('base_path', e.target.value)}
              error={!!errors.base_path}
              helperText={errors.base_path || 'Optional path prefix in the backend bucket. Leave blank for root access.'}
              placeholder="path/to/folder/"
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Public Access
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={data.public_read} onChange={(e) => setData('public_read', e.target.checked)} />}
                  label="Allow public read access (anyone can download files)"
                />
                <FormControlLabel
                  control={<Checkbox checked={data.public_list} onChange={(e) => setData('public_list', e.target.checked)} />}
                  label="Allow public list access (anyone can list files)"
                />
              </FormGroup>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Assign Credentials
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Select which credentials can access this virtual bucket.
              </Typography>
              {availableCredentials.length > 0 ? (
                <FormGroup>
                  {availableCredentials.map((cred) => (
                    <FormControlLabel
                      key={cred.id}
                      control={
                        <Checkbox
                          checked={data.credential_ids.includes(cred.id)}
                          onChange={() => toggleCredential(cred.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{cred.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cred.access_key_id}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              ) : (
                <Alert severity="info">
                  No credentials available. Create credentials first to assign them to this virtual bucket.
                </Alert>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button component={Link} href={`/virtual_buckets/${virtualBucket.id}`}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={processing}>
                Update Virtual Bucket
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ManagementLayout>
  )
}
